import { defineStore } from 'pinia';
import { useAuthStore } from './AuthStore';
import { useNodeStore } from './NodeStore';
import { useMainStore } from './MainStore';
import { usePodStore } from './PodStore';
import { usePodLabelStore } from './PodLabelStore';
import { usePodCommentStore } from './PodCommentStore';
import { useRecycleBinStore } from './RecycleBinStore';
import { PubSub } from '@aws-amplify/pubsub';

import Api from '../api/Api';

let Subscription = null;
let pubsubClient = null;
let userMessageSent = false;


const NONOTIFICATIONS = 0;
const YELLOWNOTIFICATIONS = 1;
const REDNOTIFICATIONS = 2;

const oneMinute = 60000;
const fiveMinutes = 300000;
const requestsMap = new Map();

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    newNotification: { type: 0 }, // 0 - none, 1 - yellow, 2 - red,
    downloads: {},
  }),
  getters: {
    getNotifications: function (state) {
      return state.notifications;
    },
    getDownloads: function (state) {
      return Object.values(state.downloads);
    },
    getActiveDownloadsCount: function (state) {
      return Object.values(state.downloads).filter(
        (d) => d.status === 'PREPARING' || d.status === 'FINALIZING',
      ).length;
    },
  },
  actions: {
    _setNewNotification: function (payload) {
      this.newNotification = {
        type: payload.type,
        tenantId: payload?.tenantId || null,
      };
    },

    upsertDownload: function (download) {
      const requestId = download.requestId;
      if (!requestId) return;

      const existing = this.downloads[requestId] || {};
      this.downloads[requestId] = { ...existing, ...download };
    },

    removeDownload: function (requestId) {
      delete this.downloads[requestId];
    },

    setDownloadsFromApi: function (apiDownloads) {
      // Merge API data into the downloads map without losing real-time updates.
      for (const download of apiDownloads) {
        const requestId = download.requestId;
        if (!requestId) continue;

        // API data is the source of truth for completed/failed jobs.
        // For active jobs, prefer existing real-time data if available.
        const isActive = download.status === 'PREPARING' || download.status === 'FINALIZING';
        if (isActive && this.downloads[requestId]) {
          // Merge: keep real-time progress, update metadata from API.
          this.downloads[requestId] = { ...download, ...this.downloads[requestId] };
        } else {
          this.downloads[requestId] = download;
        }
      }

      // Remove downloads that are no longer in the API response and not active.
      const apiRequestIds = new Set(apiDownloads.map((d) => d.requestId));
      for (const requestId of Object.keys(this.downloads)) {
        if (!apiRequestIds.has(requestId)) {
          delete this.downloads[requestId];
        }
      }
    },

    refreshDownloads: async function () {
      try {
        const result = await Api.download.getDownloadProgress();
        this.setDownloadsFromApi(result.data || []);
      } catch (err) {
        console.error('Failed to refresh downloads:', err);
      }
    },

    refreshNotifications: async function (payload) {
      const result = await Api.notification.getNotifications(payload);
      const notifications = result.data;

      this.notifications = notifications.sort(function (a, b) {
        let dateA = new Date(a.createdat);
        let dateB = new Date(b.createdat);
        if (dateA < dateB) return 1;
        if (dateA > dateB) return -1;
        return 0;
      });

      this._setNewNotification({ type: NONOTIFICATIONS });
    },
    unsubscribe: function () {
      if (Subscription) {
        Subscription.unsubscribe();
      }
    },
    subscribe: async function () {
      try {
        const result_1 = await Api.notification.getNotificationTopics();
        if (!pubsubClient) {
          pubsubClient = new PubSub(result_1.data.settings);
        }

        Subscription = pubsubClient
          .subscribe({ topics: result_1.data.topics })
          .subscribe({
            next: data_1 => this.onMessageReceived(data_1),
            error: error_1 => console.error(error_1),
            close: () => console.log('Done'),
          });
      } catch (err) {
        return console.error(err);
      }
    },
    onMessageReceived: function (data) {
      // console.log("Got new notification", data);
      switch (data['Symbol(topic)']) {
        case 'broadcast':
          console.log('Broadcast received', data);
          break; // system broadcasted messages
        default:
          // If there is a link to the message, we will fetch the data from a server and use it as a message.
          if (data.responseDataLink) {
            fetch(data.responseDataLink)
              .then(response => {
                if (!response.ok) {
                  throw new Error('Network response to get push notification message was not OK.');
                }

                return response.json();
              })
              .then(messageData => {
                this.checkContext(messageData);
              })
              .catch(error => {
                console.error('Fetch error:', error);
              });
          } else {
            this.checkContext(data);
          }
          break;
      }
    },
    checkContext: function (notification) {
      const authStore = useAuthStore();
      const mainStore = useMainStore();
      const podStore = usePodStore();
      const nodeStore = useNodeStore();
      const recycleBinStore = useRecycleBinStore();
      const podCommentStore = usePodCommentStore();

      let payload;

      switch (notification.context) {

        case 'general-error': {
          const snack = {
            i18n: 'common.generalError',
            color: 'error',
          };

          if (notification.message && notification.message.code) {
            delete snack.i18n;
            snack.replaceTCode = notification.message.code;
            snack.replaceTParams = notification.message.params;
            snack.replaceTFallback = 'common.generalError';
          }

          mainStore.createSnackbar(snack);
        }
          break;

        case 'state-message-updated':
          podStore.updateStateMessage(notification);
          break;

        case 'service-started':
          podStore.addLaunchedService(notification);
          podStore.updateStateMessage(notification);
          break;

        case 'update-launched-services':
          podStore.updateLaunchedServices(notification);
          break;

        case 'pod-updated':
          console.log('Notification: ', notification);
          if (notification.error) {

            if (notification.error == 'NM27' && notification.message && notification.message.code) {
              const snack = {
                i18n: 'common.serviceError',
                replaceTCode: notification.message.code,
                replaceTParams: notification.message.params,
                replaceTFallback: 'common.serviceUnknown',
                color: 'error',
              };
              mainStore.createSnackbar(snack);

              // Clean the messages after 3 seconds, if it is latest one in the message queue.
              if (notification.queue && notification.queue === 'end') {
                setTimeout(() => {
                  podStore._removeLaunchedServices(notification);
                }, 3000);
              }

              if (notification.pod) {
                podStore.updateOrIgnorePod(notification.pod);
              }
              return;
            }

            console.log('Unexpected error occured: ', notification);
            return;
          }

          podStore.addOrUpdatePod(notification.pod || notification.pods);

          if (notification.message) {
            this._setNewNotification({ type: YELLOWNOTIFICATIONS });
          }

          // Refresh ML labels
          if (notification.code && notification.code == 'AM38' && notification.message && notification.message.code && notification.message.code == 'NM31') {
            const viewPod = podStore.getViewPod;
            if (viewPod && viewPod.id && viewPod.id == notification.pod.id) {
              const podLabelStore = usePodLabelStore();
              podLabelStore.refreshMlLabels(notification.pod.id);
            }
          }

          if (notification && notification.code && notification.code == 'AM21' && notification.message && notification.message.code && notification.message.code == 'NM22') {
            const snack = {
              // i18n: "common.serviceError",
              replaceTCode: 'fileConversionComplete',
              replaceTParams: { p1: notification.pod.name },
              // replaceTFallback: "common.serviceUnknown",
              color: 'success',
              showPodAssetsOnClick: true,
              pod: notification.pod,
            };
            mainStore.createSnackbar(snack);
          }

          // Clean the messages after 3 seconds, if it is the latest one in the message queue.
          if (notification.queue && notification.queue === 'end') {
            setTimeout(() => {
              podStore._removeLaunchedServices(notification);
            }, 3000);
          }

          break;
        case 'user-updated':
          authStore.refreshDBUser();
          break;

        case 'limit-exceeded':
          this.limitExceeded(notification);
          break;

        case 'node-created':
          nodeStore.nodeCreatedNotification(notification.node);
          break;
        case 'node-updated':
          nodeStore.nodeUpdatedNotification(notification.nodes || notification.node || []);
          break;
        case 'node-deleted':
          if (Array.isArray(notification?.nodes) && notification.nodes.length > 0) {
            payload = notification.nodes;
          } else if (notification?.node) {
            payload = [ notification.node ];
          } else {
            payload = [];
          }

          podStore.removePodsByNodeDeleted(payload);
          nodeStore.nodeDeletedNotification(payload);

          // if (notification?.hasUserMessage) {
          //   this._setNewNotification({
          //     type: YELLOWNOTIFICATIONS,
          //     tenantId: notification.tenantId,
          //   });
          // }
          break;
        case 'node-moved':
          nodeStore.nodeMovedNotification(notification?.nodes || []);
          break;

        case 'node-renamed':
          nodeStore.updateNodeName(notification?.node);
          break;

        case 'pod-copied':
          podStore.podCopiedNotification(notification?.pods || []);
          break;
        case 'pod-moved':
          podStore.podMovedNotification(notification?.pods || []);
          break;
        case 'pod-blocked':
          podStore.triggerPodBlockedEvent(notification?.pods || []);
          break;
        case 'pod-deleted': {
          const numberOfFiles = notification?.pods?.length || 0;
          if (numberOfFiles > 0 && notification?.contextNodeId) {
            nodeStore.decreasePodCountOfNode(notification?.contextNodeId, numberOfFiles);
          }

          podStore.podDeletedNotification(notification?.pods || []);
          break;
        }
        case 'pod-permanently-deleted':
          recycleBinStore.podPermanentlyDeletedNotification(notification.podids);
          break;
        case 'node-copied':
          nodeStore.nodeCopiedNotification(notification?.nodes || []);
          break;
        case 'user-disabled':
          authStore.disableUser();
          break;
        case 'comments-updated':
          podCommentStore.promptRefreshComments(notification.podid);
          break;
        case 'message-received':
          this._setNewNotification({
            type: YELLOWNOTIFICATIONS,
            tenantId: notification.tenantId,
          });
          break;
        case 'download-archive':
          this.handleDownloadArchiveNotification(notification);
          break;
        case 'pod-status-changed':
          podStore.setPodStatus({
            podid: notification.podid,
            status: notification.status,
            metadata: notification.metadata,
          });
          break;
        case 'convert-image':
          if (notification.message.code === 'NM57') {
            // Image was converted successfully
            mainStore.createSnackbar({
              replaceTCode: notification.message.code,
              replaceTParams: {
                p1: notification.message.params.p1, // Name of the file
                p2: notification.message.params.p2, // Signed download url
              },
              replaceTFallback: notification.message.content,
              timeout: 10,
              color: 'success',
            });
            window.open(notification.message.params.p2, '_blank');

            const viewPod = podStore.getViewPod;
            if (viewPod) {
              podStore.fetchPodById(viewPod.id);
            }
          } else if (notification.message.code === 'NM58') {
            // Failed to convert the image
            mainStore.createSnackbar({
              replaceTCode: notification.message.code,
              replaceTParams: {
                p1: notification.message.params.p1, // Name of the file
                p2: notification.message.params.p2, // Output format
              },
              replaceTFallback: notification.message.content,
              color: 'error',
            });
          }
          break;
        case 'list-archive-contents': {
          const viewPod = podStore.getViewPod;
          if (viewPod && viewPod.id === notification.podId) {
            if (notification.message?.code === 'NM59' || notification.message?.code === 'NM66') {
              podStore.setViewPodListArchiveContentsError(notification);
            } else {
              podStore.setViewPodArchiveContents(notification.contents);
            }
          }
        } break;
        case 'file-deleted-during-processing': {
          if (notification.message.code === 'NM60') {
            mainStore.createSnackbar({
              replaceTCode: notification.message.code,
              replaceTParams: notification.message.params,
              replaceTFallback: notification.message.content,
              color: 'error',
            });
          }
        } break;
        default:
          break;
      }

      if (notification?.hasUserMessage && !userMessageSent) {
        userMessageSent = true;

        this._setNewNotification({
          type: YELLOWNOTIFICATIONS,
          tenantId: notification.tenantId,
        });

        setTimeout(() => {
          userMessageSent = false;
        }, 3000);
      }

      if (notification?.message) {
        if (notification.message?.type === 'error') {
          const snack = {
            color: 'error',
          };

          if (notification.message?.message?.code) {
            snack.replaceTCode = notification.message.message.code;
            snack.replaceTParams = notification.message?.message?.params;
            snack.replaceTFallback = 'common.generalError';
          } else {
            snack.i18n = 'common.generalError';
          }

          mainStore.createSnackbar(snack);
        }
      }

      // Remove older than 5 minutes requests from the map.
      const now = new Date();
      for (const [ requestId, timestamp ] of requestsMap.entries()) {
        if (now - timestamp > fiveMinutes) {
          requestsMap.delete(requestId);
        }
      }
    },

    handleDownloadArchiveNotification: function (notification) {
      const requestId = notification?.requestId;
      const messageCode = notification?.message?.code;

      if (!requestId) return;

      // Update the downloads map with real-time progress.
      this.upsertDownload({
        requestId,
        status: notification.status || null,
        percentage: notification.percentage ?? null,
        processedPods: notification.processedPods ?? null,
        etaSeconds: notification.etaSeconds ?? null,
        elapsedSeconds: notification.elapsedSeconds ?? null,
        nodeCount: notification.nodeCount ?? null,
        podCount: notification.podCount ?? null,
        messageCode,
        messageParams: notification?.message?.params || {},
        messageContent: notification?.message?.content || null,
      });

      if (messageCode === 'NM52') {
        // Download ready — auto-open and deduplicate.
        if (requestsMap.has(requestId)) {
          const timestamp = requestsMap.get(requestId);
          const now = new Date();

          if (now - timestamp > oneMinute) {
            requestsMap.delete(requestId);
          } else {
            return;
          }
        }

        requestsMap.set(requestId, new Date());

        this.upsertDownload({
          requestId,
          status: 'READY',
          signedUrl: notification.message.params.p1,
          expiresAt: notification.message.params.p2,
          filename: notification.message.params.p3,
        });

        window.open(notification.message.params.p1, '_blank');
      } else if (messageCode === 'NM53' || messageCode === 'NM65') {
        // Failed download.
        this.upsertDownload({
          requestId,
          status: 'FAILED',
          errorReason: notification?.message?.content || 'Download failed',
        });

        this._setNewNotification({ type: YELLOWNOTIFICATIONS, tenantId: notification.tenantId });
      }
    },

    limitExceeded: function (notification) {
      if (!notification || !notification.resource || !notification.message) {
        console.log('The limit exceeded notification is missing data!');
        return;
      }

      const snack = {
        replaceTCode: notification.message.code,
        replaceTParams: notification.message.params,
        replaceTFallback: notification.message.content,
        color: '',
      };

      if (notification.resource.spentInPercentage >= 100) {
        this._setNewNotification({ type: REDNOTIFICATIONS });
        snack.color = 'error';
      } else if (notification.resource.spentInPercentage >= 80) {
        this._setNewNotification({ type: YELLOWNOTIFICATIONS });
        snack.color = 'primary';
      }

      snack.showLimitsOnClick = true;
      const mainStore = useMainStore();
      mainStore.createSnackbar(snack);
    },
  },
});
